import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileSpreadsheet, Upload, Loader2, Trash2 } from "lucide-react";
import { API } from "@/context/AuthContext";
import { inr } from "@/pages/Dashboard";
import { GSTR2BB2BMatch } from "@/components/gst/GSTR2BB2BMatch";

export const GSTR2AImportCard = ({ extract }) => {
  const period = extract?.from?.slice(0, 7);
  const single = period && extract?.to?.slice(0, 7) === period;
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [version, setVersion] = useState(0);
  const load = useCallback(() => {
    if (!single) return;
    axios.get(`${API}/gst-returns/gstr2a/${period}`).then((r) => setDoc(r.data)).catch(() => setDoc(null));
  }, [period, single]);
  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("period", period);
    setBusy(true);
    try {
      const r = (await axios.post(`${API}/gst-returns/gstr2a/upload`, fd)).data;
      toast.success(`GSTR-2A ${r.period}: ${r.b2b_count} invoices / notes · tax ${inr(r.b2b_tax)}`);
      load(); setVersion((v) => v + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); } finally { setBusy(false); }
  };
  const remove = async () => {
    try { await axios.delete(`${API}/gst-returns/gstr2a/${period}`); toast.success("GSTR-2A removed"); setDoc(null); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove"); }
  };

  if (!extract) return null;
  return (
    <div className="space-y-4">
      <Card className="border-slate-200 shadow-sm" data-testid="gstr2a-import-card">
        <CardHeader className="pb-2 flex flex-row items-start justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="text-base flex items-center gap-2"><FileSpreadsheet className="h-4 w-4" /> GSTR-2A — live supplier filings vs books</CardTitle>
            <p className="text-[11px] text-slate-500 mt-1 max-w-3xl">Upload the month's GSTR-2A (Excel or JSON from the GST portal). Its B2B invoices and CDNR notes are matched to SAP the same way as GSTR-2B.</p>
          </div>
          {single ? (
            <div className="flex items-center gap-2">
              <label className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs cursor-pointer ${busy ? "opacity-60" : "hover:border-sky-400"}`} data-testid="gstr2a-upload-label">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} {doc ? "Replace" : "Upload"} GSTR-2A for {period}
                <input type="file" accept=".xlsx,.json" className="hidden" disabled={busy} data-testid="gstr2a-file-input" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
              </label>
              {doc && <Button size="sm" variant="ghost" onClick={remove} data-testid="gstr2a-delete-btn"><Trash2 className="h-4 w-4 text-rose-600" /></Button>}
            </div>
          ) : <p className="text-[11px] text-amber-700">GSTR-2A works on a single month — use a one-month extraction.</p>}
        </CardHeader>
        <CardContent>
          {doc ? <p className="text-xs text-slate-700" data-testid="gstr2a-summary">{doc.b2b_count} B2B / CDNR entries · tax {inr(doc.b2b_tax)} · {doc.filename} · uploaded {new Date(doc.uploaded_at).toLocaleString("en-IN")} by {doc.uploaded_by}</p>
            : <p className="text-xs text-slate-500" data-testid="gstr2a-empty">No GSTR-2A uploaded for this month.</p>}
        </CardContent>
      </Card>
      {doc && <GSTR2BB2BMatch period={period} version={version} source="gstr2a" />}
    </div>
  );
};
